import { Head, Link, router, usePoll } from "@inertiajs/react"
import {
  ArrowLeft,
  Video,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  Radio,
  Sparkles,
  FileText,
  ExternalLink,
  RotateCcw,
  User,
  Building2,
  Briefcase,
  Globe,
  Trash2,
  Download,
} from "lucide-react"

import { useEffect, useState } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

const statusSteps = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "bot_joining", label: "Bot Joining", icon: Loader2 },
  { key: "recording", label: "Recording", icon: Radio },
  { key: "processing_transcript", label: "Processing", icon: FileText },
  { key: "generating_article", label: "Generating", icon: Sparkles },
  { key: "completed", label: "Completed", icon: CheckCircle },
]

const statusOrder = {
  pending: 0,
  bot_joining: 1,
  recording: 2,
  processing_transcript: 3,
  awaiting_claim: 3.5, // processing done, generating not started; see the notice in the Status card
  generating_article: 4,
  completed: 5,
  failed: -1,
}

function StatusTimeline({ status }) {
  const currentIndex = statusOrder[status]
  const isFailed = status === "failed"

  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {statusSteps.map((step, index) => {
        const isCompleted = !isFailed && currentIndex > index
        const isCurrent = !isFailed && currentIndex === index
        const Icon = step.icon

        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center min-w-[80px]">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isCompleted
                    ? "bg-green-100 text-green-600"
                    : isCurrent
                    ? "bg-kf-red/10 text-kf-red ring-2 ring-kf-red/20"
                    : "bg-zinc-100 text-zinc-400"
                }`}
              >
                <Icon className={`h-4 w-4 ${isCurrent ? "animate-pulse" : ""}`} />
              </div>
              <span
                className={`text-xs mt-1 text-center ${
                  isCompleted || isCurrent
                    ? "text-zinc-900 font-medium"
                    : "text-zinc-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < statusSteps.length - 1 && (
              <div
                className={`h-0.5 w-6 mt-[-18px] ${
                  isCompleted ? "bg-green-300" : "bg-zinc-200"
                }`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function formatDateTime(dateString) {
  if (!dateString) return null
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function TimelineEvent({ label, timestamp }) {
  if (!timestamp) return null
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-zinc-600">{label}</span>
      <span className="text-zinc-900 font-medium">{formatDateTime(timestamp)}</span>
    </div>
  )
}

export default function MeetingShow({ currentUser, meeting }) {
  // Mirrors Meeting.active: awaiting_claim is parked, nothing to poll for.
  const isActive = !["completed", "failed", "skipped", "archived", "awaiting_claim"].includes(meeting.status)
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Poll for updates when meeting is active
  const { start, stop } = usePoll(
    8000,
    { only: ["meeting"] },
    { autoStart: false }
  )

  useEffect(() => {
    if (isActive) {
      start()
    } else {
      stop()
    }
    return () => stop()
  }, [isActive])

  const handleRetry = () => {
    router.post(`/app/coach/meetings/${meeting.id}/retry_generation`)
  }

  const handleDelete = () => {
    setDeleteOpen(false)
    router.delete(`/app/coach/meetings/${meeting.id}`)
  }

  const handleDownloadTranscript = () => {
    if (!meeting.transcript) return
    const dateSource = meeting.recordingStartedAt || meeting.createdAt
    const date = dateSource ? new Date(dateSource) : new Date()
    const datePart = date.toLocaleDateString("en-CA")
    const filename = `transcript-${meeting.id}-${datePart}.txt`
    const blob = new Blob([meeting.transcript], { type: "text/plain;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`Meeting ${meeting.intervieweeName ? `- ${meeting.intervieweeName}` : ""} - Kingdom Factor`} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            href="/app/coach/meetings"
            className="p-2 rounded-md hover:bg-zinc-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-zinc-900">
              {meeting.intervieweeName
                ? `Interview: ${meeting.intervieweeName}`
                : "Meeting Recording"}
            </h1>
            <p className="text-zinc-600">
              {meeting.source === "recall_bot" ? "Bot Recording" : "Manual Upload"} &middot;{" "}
              {formatDateTime(meeting.createdAt)}
            </p>
          </div>
          {meeting.canRetry && (
            <Button data-tour="generate-article-btn" variant="gradient" onClick={handleRetry}>
              {meeting.status === "skipped" ? (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Article
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  Retry Generation
                </>
              )}
            </Button>
          )}
          {meeting.articleId && (
            <Link href={`/app/coach/articles/${meeting.articleId}`}>
              <Button variant="gradient">
                <ExternalLink className="h-4 w-4" />
                Edit Article
              </Button>
            </Link>
          )}
          {meeting.isOwner !== false && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setDeleteOpen(true)}
              className="text-zinc-500 hover:text-red-600 hover:border-red-200"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status</CardTitle>
          </CardHeader>
          <CardContent>
            {meeting.status === "skipped" ? (
              <div className="space-y-3">
                <Badge
                  variant="outline"
                  className="bg-zinc-100 text-zinc-600 border-zinc-200"
                >
                  <FileText className="h-3 w-3" />
                  Not an Interview
                </Badge>
                <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                  <p className="text-sm text-zinc-700">
                    {meeting.errorMessage || "This transcript doesn't appear to be an interview, so no article was generated."}
                  </p>
                </div>
                {meeting.canRetry && (
                  <p className="text-sm text-zinc-600">
                    Think the AI got it wrong? You can force article generation using the button above.
                  </p>
                )}
              </div>
            ) : meeting.status === "failed" ? (
              <div className="space-y-3">
                <Badge
                  variant="outline"
                  className="bg-red-50 text-red-700 border-red-200"
                >
                  <AlertCircle className="h-3 w-3" />
                  Failed
                </Badge>
                {meeting.errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{meeting.errorMessage}</p>
                  </div>
                )}
                {meeting.canRetry && (
                  <p className="text-sm text-zinc-600">
                    The transcript was captured successfully. You can retry article generation.
                  </p>
                )}
              </div>
            ) : meeting.status === "archived" ? (
              <div className="space-y-3">
                <Badge
                  variant="outline"
                  className="bg-zinc-100 text-zinc-600 border-zinc-200"
                >
                  <FileText className="h-3 w-3" />
                  Archived
                </Badge>
                <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                  <p className="text-sm text-zinc-700">
                    {meeting.errorMessage || "The original article was deleted."}
                  </p>
                </div>
                {meeting.canRetry && (
                  <p className="text-sm text-zinc-600">
                    The transcript is still here — you can regenerate a new article.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <StatusTimeline status={meeting.status} />
                {meeting.status === "awaiting_claim" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      Two coaches recorded this call, so nothing has been generated yet.
                      Claim it from your <Link href="/app/coach/meetings" className="underline">Meetings page</Link> if
                      the article is yours.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Interviewee Details */}
          <Card data-tour="interviewee-details-card">
            <CardHeader>
              <CardTitle className="text-lg">Interviewee Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {meeting.intervieweeName ? (
                <>
                  <div className="flex items-center gap-3 text-sm">
                    <User className="h-4 w-4 text-zinc-400" />
                    <span className="text-zinc-900">{meeting.intervieweeName}</span>
                  </div>
                  {meeting.intervieweeJobTitle && (
                    <div className="flex items-center gap-3 text-sm">
                      <Briefcase className="h-4 w-4 text-zinc-400" />
                      <span className="text-zinc-900">{meeting.intervieweeJobTitle}</span>
                    </div>
                  )}
                  {meeting.intervieweeCompany && (
                    <div className="flex items-center gap-3 text-sm">
                      <Building2 className="h-4 w-4 text-zinc-400" />
                      <span className="text-zinc-900">{meeting.intervieweeCompany}</span>
                    </div>
                  )}
                  {meeting.intervieweeCompanyWebsite && (
                    <div className="flex items-center gap-3 text-sm">
                      <Globe className="h-4 w-4 text-zinc-400" />
                      <a
                        href={meeting.intervieweeCompanyWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-kf-red hover:underline"
                      >
                        {meeting.intervieweeCompanyWebsite}
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-zinc-500">
                  Interviewee details will be extracted from the transcript.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Event Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <TimelineEvent label="Created" timestamp={meeting.createdAt} />
                <TimelineEvent label="Bot Joined" timestamp={meeting.botJoinedAt} />
                <TimelineEvent label="Recording Started" timestamp={meeting.recordingStartedAt} />
                <TimelineEvent label="Recording Ended" timestamp={meeting.recordingEndedAt} />
                <TimelineEvent label="Transcript Received" timestamp={meeting.transcriptReceivedAt} />
                <TimelineEvent label="Article Generated" timestamp={meeting.articleGeneratedAt} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Key Focus Areas */}
        {meeting.keyFocusAreas && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Key Focus Areas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap">
                {meeting.keyFocusAreas}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Transcript */}
        {meeting.transcript && (
          <Card data-tour="transcript-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg">Transcript</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTranscript}
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-y-auto bg-zinc-50 rounded-lg p-4 border border-zinc-200">
                <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {meeting.transcript}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the meeting recording and any associated data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
